import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
export const workforceProtectedFormsDisplayValueSchema = z.strictObject({
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['standard', 'wide']),
    fieldEditorColumns: z.union([z.literal(1), z.literal(2)]),
    showVersionHistory: z.boolean(),
});
export const workforceProtectedFormsDisplaySetting = definePlatformSetting({
    definition: {
        key: 'workforce.protected-forms.display', moduleId: 'workforce',
        title: 'Employee form definition display',
        helpText: 'Controls the tenant administrator definition editor and version-history layout.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: { density: 'comfortable', cardSize: 'wide', fieldEditorColumns: 2, showVersionHistory: true },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: false, safetyClass: 'ordinary',
    }, valueSchema: workforceProtectedFormsDisplayValueSchema,
});
export const workforceProtectedFormsCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().min(1).max(400),
    createAction: z.string().trim().min(1).max(80),
    appendAction: z.string().trim().min(1).max(80),
    publishAction: z.string().trim().min(1).max(80),
    archiveAction: z.string().trim().min(1).max(80),
    restoreAction: z.string().trim().min(1).max(80),
    rollbackAction: z.string().trim().min(1).max(80),
    keyLabel: z.string().trim().min(1).max(80),
    titleLabel: z.string().trim().min(1).max(80),
    descriptionLabel: z.string().trim().min(1).max(80),
    purposeLabel: z.string().trim().min(1).max(80),
    sensitivityLabel: z.string().trim().min(1).max(80),
    audienceLabel: z.string().trim().min(1).max(80),
    formAssetLabel: z.string().trim().min(1).max(100),
    chooseAssetLabel: z.string().trim().min(1).max(120),
    versionLabel: z.string().trim().min(1).max(60),
    newFieldLabel: z.string().trim().min(1).max(80),
    defaultChoiceLabel: z.string().trim().min(1).max(80),
    fieldDefinitionsTitle: z.string().trim().min(1).max(120),
    addFieldAction: z.string().trim().min(1).max(80),
    removeFieldAction: z.string().trim().min(1).max(80),
    fieldKeyLabel: z.string().trim().min(1).max(80),
    fieldLabelLabel: z.string().trim().min(1).max(80),
    fieldTypeLabel: z.string().trim().min(1).max(80),
    requiredLabel: z.string().trim().min(1).max(80),
    sensitiveLabel: z.string().trim().min(1).max(80),
    optionsLabel: z.string().trim().min(1).max(100),
    consentTextLabel: z.string().trim().min(1).max(100),
    consentFallback: z.string().trim().min(1).max(4000),
    maximumAttachmentsLabel: z.string().trim().min(1).max(100),
    attachmentTypesLabel: z.string().trim().min(1).max(100),
    reasonLabel: z.string().trim().min(1).max(80),
    createReasonFallback: z.string().trim().min(10).max(500),
    appendReasonFallback: z.string().trim().min(10).max(500),
    versionHistoryTitle: z.string().trim().min(1).max(100),
    loadingLabel: z.string().trim().min(1).max(120),
    savingLabel: z.string().trim().min(1).max(120),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    documentsUnavailable: z.string().trim().min(1).max(240),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(300),
    savedLabel: z.string().trim().min(1).max(160),
    statusLabels: z.strictObject({
        draft: z.string().trim().min(1).max(60),
        published: z.string().trim().min(1).max(60),
        archived: z.string().trim().min(1).max(60),
    }),
    purposeLabels: z.strictObject({
        onboarding: z.string().trim().min(1).max(80),
        'tax-withholding': z.string().trim().min(1).max(80),
        'employment-eligibility': z.string().trim().min(1).max(80),
        'payroll-banking': z.string().trim().min(1).max(80),
        incident: z.string().trim().min(1).max(80),
        'performance-review': z.string().trim().min(1).max(80),
        'corrective-action': z.string().trim().min(1).max(80),
        offboarding: z.string().trim().min(1).max(80),
        'other-personnel': z.string().trim().min(1).max(80),
    }),
    sensitivityLabels: z.strictObject({
        confidential: z.string().trim().min(1).max(80),
        tax: z.string().trim().min(1).max(80),
        'employment-eligibility': z.string().trim().min(1).max(80),
        banking: z.string().trim().min(1).max(80),
        medical: z.string().trim().min(1).max(80),
        'personnel-restricted': z.string().trim().min(1).max(80),
    }),
    audienceLabels: z.strictObject({
        'self-service': z.string().trim().min(1).max(80),
        'manager-assigned': z.string().trim().min(1).max(80),
        'self-or-manager': z.string().trim().min(1).max(80),
    }),
    fieldTypeLabels: z.strictObject({
        text: z.string().trim().min(1).max(80),
        multiline: z.string().trim().min(1).max(80),
        date: z.string().trim().min(1).max(80),
        currency: z.string().trim().min(1).max(80),
        boolean: z.string().trim().min(1).max(80),
        choice: z.string().trim().min(1).max(80),
        'multi-choice': z.string().trim().min(1).max(80),
        number: z.string().trim().min(1).max(80),
        file: z.string().trim().min(1).max(80),
    }),
});
export const workforceProtectedFormsCopySetting = definePlatformSetting({
    definition: {
        key: 'workforce.copy.protected-forms', moduleId: 'workforce',
        title: 'Employee form definition copy',
        helpText: 'Controls tenant-editable labels for protected employee-form definitions and lifecycle.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Employee form setup', title: 'Employee form definitions',
            description: 'Define who may complete each approved Documents form without storing submitted field values in Workforce.',
            createAction: 'Create form definition', appendAction: 'Save new definition version',
            publishAction: 'Publish selected version', archiveAction: 'Archive definition',
            restoreAction: 'Restore definition', rollbackAction: 'Roll back as new version',
            keyLabel: 'Definition key', titleLabel: 'Employee-facing title', descriptionLabel: 'Description',
            purposeLabel: 'Purpose', sensitivityLabel: 'Sensitivity', audienceLabel: 'Audience',
            formAssetLabel: 'Published Documents form file',
            chooseAssetLabel: 'Choose a published Documents form', versionLabel: 'Version',
            newFieldLabel: 'Field', defaultChoiceLabel: 'Option 1', fieldDefinitionsTitle: 'Completion fields',
            addFieldAction: 'Add completion field', removeFieldAction: 'Remove field',
            fieldKeyLabel: 'Field key', fieldLabelLabel: 'Employee label', fieldTypeLabel: 'Field type',
            requiredLabel: 'Required', sensitiveLabel: 'Contains protected information',
            optionsLabel: 'Choice options, one per line', consentTextLabel: 'Employee consent statement',
            consentFallback: 'I certify that the completed form and signature are accurate.',
            maximumAttachmentsLabel: 'Maximum supporting files', attachmentTypesLabel: 'Allowed supporting file types',
            reasonLabel: 'Change reason',
            createReasonFallback: 'Create protected employee form definition',
            appendReasonFallback: 'Save protected employee form definition version',
            versionHistoryTitle: 'Definition version history',
            loadingLabel: 'Loading employee form definitions…', savingLabel: 'Saving employee form definition…',
            loadError: 'Employee form definitions could not be loaded.',
            mutationError: 'The employee form definition could not be saved. Refresh and try again.',
            documentsUnavailable: 'The Documents module or its approved form files are unavailable. Existing definitions remain readable.',
            emptyTitle: 'No employee form definitions yet',
            emptyDescription: 'Publish an approved Documents form file, then create its Workforce eligibility definition.',
            savedLabel: 'The employee form definition action completed successfully.',
            statusLabels: { draft: 'Draft', published: 'Published', archived: 'Archived' },
            purposeLabels: {
                onboarding: 'Onboarding', 'tax-withholding': 'Tax withholding',
                'employment-eligibility': 'Employment eligibility', 'payroll-banking': 'Payroll banking',
                incident: 'Incident', 'performance-review': 'Performance review',
                'corrective-action': 'Corrective action', offboarding: 'Offboarding',
                'other-personnel': 'Other personnel',
            },
            sensitivityLabels: {
                confidential: 'Confidential', tax: 'Tax',
                'employment-eligibility': 'Employment eligibility', banking: 'Banking',
                medical: 'Medical', 'personnel-restricted': 'Personnel restricted',
            },
            audienceLabels: {
                'self-service': 'Self service', 'manager-assigned': 'Manager assigned',
                'self-or-manager': 'Self or manager',
            },
            fieldTypeLabels: {
                text: 'Text', multiline: 'Long text', date: 'Date', number: 'Number',
                currency: 'Currency', boolean: 'Yes or no', choice: 'Single choice',
                'multi-choice': 'Multiple choice', file: 'File',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    }, valueSchema: workforceProtectedFormsCopyValueSchema,
});
//# sourceMappingURL=workforce-protected-form-setting-definitions.js.map